import type { CSSProperties } from 'react';

import { LiceBox } from '../fishObservationGallery.types.ts';
import {
  ORBIT_ONE_ANNOTATION_STROKE,
  orbitOneAnnotationDefaultStroke,
  orbitOneAnnotationStrokeByType,
} from './orbitOneAnnotationOverlay.config.ts';

type OrbitOneAnnotationOverlayProps = {
  boxes: LiceBox[];
  /**
   * The crop image's intrinsic pixel size `[width, height]` — the space lice boxes are stored in
   * (original-crop pixel space). The SVG user space matches it 1:1.
   */
  cropSize: [number, number] | undefined;
  /** Current lightbox zoom level; the box outline gets thinner as it grows. Defaults to 1. */
  zoom?: number;
};

/**
 * SVG overlay drawing lice bounding boxes over a fish crop. Rendered as a sibling of the crop image
 * inside the lightbox's zoom wrapper, so the shared CSS transform keeps every box locked to the
 * image through zoom and pan. The `viewBox` is the crop's intrinsic pixel size, so detection
 * coordinates (already in crop pixels) are plotted directly; `vectorEffect` keeps strokes a fixed
 * screen-pixel width, which we taper down as the user zooms in so boxes stay tight on the lice.
 *
 * `preserveAspectRatio="xMidYMid meet"` mirrors the image's `object-fit: contain`: both the image
 * and the overlay are letterboxed and centred the same way inside their shared box, so the boxes
 * stay aligned even when that box is wider/taller than the image (e.g. fullscreen on an ultra-wide
 * monitor). With `none` the SVG would stretch to the box and drift off the image.
 */
export const OrbitOneAnnotationOverlay = ({
  boxes,
  cropSize,
  zoom = 1,
}: OrbitOneAnnotationOverlayProps) => {
  const [width, height] = cropSize ?? [];
  if (!width || !height || boxes.length === 0) return null;

  const strokeWidth = strokeWidthForZoom(zoom);
  const strokeOpacity = strokeOpacityForZoom(zoom);

  return (
    <svg
      style={overlayStyle}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio='xMidYMid meet'
      aria-hidden
    >
      {boxes.map(({ bbox, type }, boxIndex) => {
        const { x, y, boxWidth, boxHeight } = normalizeBbox(bbox);
        return (
          <rect
            key={boxIndex}
            x={x}
            y={y}
            width={boxWidth}
            height={boxHeight}
            fill='none'
            stroke={
              (type && orbitOneAnnotationStrokeByType[type]) ?? orbitOneAnnotationDefaultStroke
            }
            strokeWidth={strokeWidth}
            strokeOpacity={strokeOpacity}
            vectorEffect='non-scaling-stroke'
          />
        );
      })}
    </svg>
  );
};

const overlayStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  // Let zoom/pan pointer gestures pass through to the image beneath.
  pointerEvents: 'none',
};

const strokeWidthForZoom = (zoom: number): number =>
  Math.max(
    ORBIT_ONE_ANNOTATION_STROKE.minWidth,
    Math.min(ORBIT_ONE_ANNOTATION_STROKE.baseWidth, ORBIT_ONE_ANNOTATION_STROKE.baseWidth / zoom)
  );

/** Fully opaque at 1×, fading toward {@link ORBIT_ONE_ANNOTATION_STROKE.minOpacity} as you zoom in. */
const strokeOpacityForZoom = (zoom: number): number =>
  Math.max(
    ORBIT_ONE_ANNOTATION_STROKE.minOpacity,
    1 - (zoom - 1) * ORBIT_ONE_ANNOTATION_STROKE.opacityFalloff
  );

const normalizeBbox = (bbox: LiceBox['bbox']) => {
  const [x1, y1, x2, y2] = bbox;
  return {
    x: Math.min(x1, x2),
    y: Math.min(y1, y2),
    boxWidth: Math.abs(x2 - x1),
    boxHeight: Math.abs(y2 - y1),
  };
};
